import { Injectable } from '@angular/core';
import {
  CommercialOutage,
  CommercialOutageConnection,
  CommercialOutageSearch,
} from 'src/app/model/commercial-services/commercial-outage';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { CommercialFacilityBuilderService } from './commercial-facility-builder.service';
import {
  CommercialOutageMessageKeys,
  ValidationConfig,
} from 'src/app/core/type-helpers/validation-msg';
import { inject } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class CommercialOutageBuilderService {
  private _commercialFacilityBuilder = inject(CommercialFacilityBuilderService);

  buildCommercialOutageValidationMessages(): ValidationConfig<CommercialOutageMessageKeys> {
    return {
      messages: {
        outageStartDate: {
          required: 'COMMERCIAL_SERVICES.VALIDATION.OUTAGE_START_DATE_REQUIRED',
        },
        outageEndDate: {
          required: 'COMMERCIAL_SERVICES.VALIDATION.OUTAGE_END_DATE_REQUIRED',
        },
        numberOfUnitsOnOutage: {
          required:
            'COMMERCIAL_SERVICES.VALIDATION.NUMBER_OF_UNITS_ON_OUTAGE_REQUIRED',
        },
        numberOfTotalUnits: {
          required:
            'COMMERCIAL_SERVICES.VALIDATION.NUMBER_OF_TOTAL_UNITS_REQUIRED',
        },
        aorId: {
          required: 'COMMERCIAL_SERVICES.VALIDATION.AOR_ID_REQUIRED',
        },
        commercialFacilityId: {
          required:
            'COMMERCIAL_SERVICES.VALIDATION.COMMERCIAL_FACILITY_ID_REQUIRED',
        },
        commercialOperatingAreaId: {
          required:
            'COMMERCIAL_SERVICES.VALIDATION.COMMERCIAL_OPERATING_AREA_ID_REQUIRED',
        },
        commercialMarketAreaId: {
          required:
            'COMMERCIAL_SERVICES.VALIDATION.COMMERCIAL_MARKET_AREA_ID_REQUIRED',
        },
      },
      params: {},
    };
  }

  buildNewCommercialOutage(): CommercialOutage {
    return {
      id: '',
      outageStartDate: new Date(),
      outageEndDate: new Date(),
      numberOfUnitsOnOutage: 0,
      numberOfTotalUnits: 0,
      commercialFacilityId: '',
      aorId: '',
      commercialOperatingAreaId: '',
      commercialMarketAreaId: '',
      notes: '',
      createdBy: '',
      createdAt: new Date(),
      updater: '',
      updatedAt: new Date(),
      deletedAt: new Date(),
      version: 0,
    } as unknown as CommercialOutage;
  }

  buildCommercialOutage(i: any): CommercialOutage {
    if (isNullOrUndefined(i)) {
      throw new Error(
        'Cannot build a commercial outage from a missing response.'
      );
    }

    const facility = this._commercialFacilityBuilder.buildCommercialFacility(
      i.CommercialFacility
    );

    if (facility === null) {
      throw new Error(`Commercial outage ${i.id} is missing its facility.`);
    }

    if (isNullOrUndefined(i.AOR)) {
      throw new Error(`Commercial outage ${i.id} is missing its AOR.`);
    }

    return {
      id: i.id,
      outageStartDate: i.outageStartDate,
      outageEndDate: i.outageEndDate,
      numberOfUnitsOnOutage: i.numberOfUnitsOnOutage,
      numberOfTotalUnits: i.numberOfTotalUnits,
      commercialFacilityId: i.commercialFacilityId,
      aorId: i.aorId,
      commercialOperatingAreaId: i.commercialOperatingAreaId,
      commercialMarketAreaId: i.commercialMarketAreaId,
      notes: i.notes,
      createdBy: i.createdBy,
      createdAt: i.createdAt,
      updater: i.updater,
      updatedAt: i.updatedAt,
      deletedAt: i.deletedAt,
      version: i.version,
      CommercialFacility: facility,
      aor: i.AOR,
      CommercialOperatingArea: i.CommercialOperatingArea,
      CommercialMarketArea: i.CommercialMarketArea,
    };
  }

  buildCommercialOutageConnection(item: any): CommercialOutageConnection {
    return {
      items: item.items.map(
        (outage: any): CommercialOutageSearch => ({
          id: outage.id,
          outageStartDate: outage.outageStartDate,
          outageEndDate: outage.outageEndDate,
          numberOfUnitsOnOutage: outage.numberOfUnitsOnOutage,
          numberOfTotalUnits: outage.numberOfTotalUnits,
          facilityName: outage.CommercialFacility?.facilityName ?? '',
          facilityId: outage.CommercialFacility?.facilityId ?? '',
          aor: outage.AOR?.name ?? '',
          commercialOperatingArea: outage.CommercialOperatingArea?.name ?? '',
          commercialMarketArea: outage.CommercialMarketArea?.name ?? '',
        })
      ),
      totalRecords: item.totalRecords,
    };
  }

  buildCommercialOutageQuery(filters: any): Record<string, string> {
    const query: Record<string, string> = {};

    Object.keys(filters).forEach((key) => {
      const filter = filters[key];
      const value = Array.isArray(filter) ? filter[0]?.value : filter?.value;
      if (value !== null && value !== undefined && value !== '') {
        query[key] =
          value instanceof Date
            ? value.toISOString()
            : typeof value === 'string'
              ? value.trim()
              : String(value);
      }
    });
    return query;
  }
}
